import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Input, Menu, MenuTrigger, MenuPopover, MenuList, MenuItem } from '@fluentui/react-components';
import axiosInstance from '../../apiServices/axios';
import styles from './css/index.module.css';
import setupStyles from '../WorkspaceSetup/WorkspaceSetupWizard.module.css';
import taskMgmtStyles from '../TaskManagement/css/index.module.css';
import {
    ArrowExit20Regular,
    ArrowSort16Regular,
    CheckmarkSquare20Filled,
    ChevronDown12Regular,
    Delete16Regular,
    Edit16Regular,
    Filter20Regular,
    Square20Regular,
} from '@fluentui/react-icons';
import DoubleConfirmationModal from '../CommonComponents/DoubleConfirmationModal/DoubleConfirmationModal';
import { updateWorkspaceDefaults } from '../../redux/usersSlice';
import { toast } from 'react-toastify';
import ProjectTypesEditor from '../CommonComponents/ProjectTypesEditor/ProjectTypesEditor';
import { normalizeProjectTypeOptions, toProjectTypeOptions, DEFAULT_WORKSPACE_PROJECT_TYPES } from '../../utils/projectTypes';
import { resolveWorkspaceLensFeatures } from '../../utils/lensFeatures';
import WorkspaceProfileSection from './WorkspaceProfileSection';
import { normalizeOrgProfile } from '../../utils/workspaceOrgProfile';
import { normalizePulseProfile, normalizeCompanyValues, PROFILE_PATCH_KEYS } from '../../utils/workspacePulseProfile';

// Lens feature configuration — aligned with workspace setup (Step 2) and project feature visibility
const REQUIRED_FEATURES = ['project_overview', 'dataroom', 'information_requests', 'project_management'];
const RECOMMENDED_FEATURES = ['journal', 'lens_intelligence'];

const AUDIT_PAGE_SIZE = 20;

/** UI label for audit category; strips legacy `WS - ` prefix if present. */
function formatAuditCategoryDisplay(category) {
    if (category == null || category === '') return '';
    return String(category).replace(/^WS\s*-\s*/, '');
}

const AUDIT_CATEGORY_OPTIONS = [
    { value: '', label: 'All categories' },
    { value: 'User & Access', label: 'User & Access' },
    { value: 'Authentication', label: 'Authentication' },
    { value: 'Configuration', label: 'Configuration' },
    { value: 'Project Lifecycle', label: 'Project Lifecycle' },
    { value: 'Security', label: 'Security' },
];

const FEATURE_CATEGORIES = {
    strategic_assessment: {
        name: 'Strategic Assessment & Monitoring',
        features: {
            project_overview: {
                name: 'Project Overview',
                description: 'Every project starts with an overview dashboard summarising deal progress, key metrics, and team activity.',
            },
            maturity_index: {
                name: 'Maturity Index',
                description: 'Assess the technology maturity of a target company using a structured scoring framework across key capability areas.',
            },
            security_index: {
                name: 'Security Index',
                description: "Evaluate the target company's security posture across infrastructure, policies, and practices.",
            },
            team_analyser: {
                name: 'Team Analyser',
                description: "Review and document the organisational structure, leadership capability, and cultural fit of the target company's team.",
            },
            security_governance: {
                name: 'Security Governance',
                description: 'Assess the governance frameworks, compliance posture, and security policies in place at the target company.',
            },
        },
    },
    due_diligence: {
        name: 'Due Diligence & Knowledge',
        features: {
            dataroom: {
                name: 'Dataroom',
                description: 'A secure, multi-workstream virtual data room for managing and sharing deal documents with granular access controls.',
            },
            information_requests: {
                name: 'Information Requests',
                description: 'Manage information and document requests across workstreams and parties. Track responses, follow-ups, and outstanding items in one place.',
            },
            dataroom_wizard: {
                name: 'Dataroom Wizard',
                description: 'A guided tool to help structure and prepare a data room.',
            },
            observations: {
                name: 'Observations',
                description: 'Capture, categorise, and manage consultant and team findings during due diligence. Linked to workstreams and deal stages.',
            },
            lessons_learnt: {
                name: 'Lessons Learnt',
                description: 'Record and access institutional knowledge from completed deals. Build a compounding knowledge base across your portfolio.',
            },
            journal: {
                name: 'Journal',
                description: 'A shared note-taking space for deal teams. Capture informal insights, meeting notes, and commentary throughout the deal lifecycle.',
            },
            lens_intelligence: {
                name: 'Lens Intelligence',
                description: 'Ask AI across documents, IRL responses, and observations with evidence-verified answers and saved chats.',
            },
        },
    },
    project_risk_management: {
        name: 'Project & Risk Management',
        features: {
            project_management: {
                name: 'Project Management',
                description: 'Full project and task management with Kanban boards, task assignment, priorities, due dates, and progress tracking.',
            },
            raid_log: {
                name: 'RAID Log',
                description: 'Track Risks, Assumptions, Issues, and Dependencies across the deal. Flag, assign, and monitor resolution in real time.',
            },
        },
    },
    technical_service: {
        name: 'Technical & Service Delivery',
        features: {
            software_inventory: {
                name: 'Software Inventory',
                description: "Document and assess the target company's software stack, licensing arrangements, and technology dependencies.",
            },
        },
    },
};

const normalizeWorkspaceProfileFields = data => {
    if (!data) return data;
    return {
        ...data,
        ...normalizeOrgProfile(data),
        ...normalizePulseProfile(data),
        company_values: normalizeCompanyValues(data.company_values),
    };
};

const WorkspaceSettings = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { workspaceId } = useParams();
    const [searchParams] = useSearchParams();
    const { memberships } = useSelector(state => state.user);

    const membershipForRoute = useMemo(() => {
        if (!Array.isArray(memberships) || memberships.length === 0) return undefined;
        // User must be a member of THIS workspace to access settings
        return memberships.find(m => String(m?.workspace?.id) === String(workspaceId));
    }, [memberships, workspaceId]);
    const isWorkspaceSuperAdmin = membershipForRoute?.role === 'WORKSPACE_SUPER_ADMIN';
    const effectiveWorkspaceId = workspaceId;

    useEffect(() => {
        if (searchParams.get('section') === 'workspace-pulse' && effectiveWorkspaceId) {
            navigate(`/workspace-pulse?workspace_id=${encodeURIComponent(effectiveWorkspaceId)}`, { replace: true });
        }
    }, [searchParams, effectiveWorkspaceId, navigate]);
    const [settings, setSettings] = useState(null);
    const [originalSettings, setOriginalSettings] = useState(null);
    const [members, setMembers] = useState([]);
    const [invitations, setInvitations] = useState([]);
    const [originalMembers, setOriginalMembers] = useState([]);
    const [projects, setProjects] = useState([]);
    const [projectTypes, setProjectTypes] = useState([]);
    const [projectStatuses, setProjectStatuses] = useState([]);
    const [newStatusName, setNewStatusName] = useState('');
    const [newStatusColor, setNewStatusColor] = useState('#8E2DE2');
    const [editingStatusIndex, setEditingStatusIndex] = useState(null);
    const [editingStatusName, setEditingStatusName] = useState('');
    const [editingStatusColor, setEditingStatusColor] = useState('#8E2DE2');
    const [draggedStatusIndex, setDraggedStatusIndex] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [editingUserId, setEditingUserId] = useState(null);
    const [editingUserAccess, setEditingUserAccess] = useState('vdr');
    const [editingUserStatus, setEditingUserStatus] = useState('active');
    const [usersTab, setUsersTab] = useState('active');
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteUserType, setInviteUserType] = useState('');
    const [inviteSubmitting, setInviteSubmitting] = useState(false);
    const [invitationActionId, setInvitationActionId] = useState(null);
    const fileInputRef = useRef(null);
    const [logoFile, setLogoFile] = useState(null);
    const [logoObjectUrl, setLogoObjectUrl] = useState(null);
    const [auditLogs, setAuditLogs] = useState([]);
    const [auditLoading, setAuditLoading] = useState(false);
    const [auditError, setAuditError] = useState('');
    const [auditSearchInput, setAuditSearchInput] = useState('');
    const [auditSearch, setAuditSearch] = useState('');
    const [auditCategory, setAuditCategory] = useState('');
    const [auditDateFrom, setAuditDateFrom] = useState('');
    const [auditDateTo, setAuditDateTo] = useState('');
    const [auditPage, setAuditPage] = useState(1);
    const [auditTotalCount, setAuditTotalCount] = useState(0);

    const [selected, setSelected] = useState('Workspace');

    useEffect(() => {
        if (!effectiveWorkspaceId) {
            navigate('/project', { replace: true });
            return;
        }

        // Wait until memberships have loaded before enforcing the guard
        if (!Array.isArray(memberships) || memberships.length === 0) {
            return;
        }

        if (!membershipForRoute || !isWorkspaceSuperAdmin) {
            navigate('/project', { replace: true });
        }
    }, [effectiveWorkspaceId, memberships, membershipForRoute, isWorkspaceSuperAdmin, navigate]);

    useEffect(() => {
        if (!isWorkspaceSuperAdmin || !effectiveWorkspaceId || !membershipForRoute) return;
        const load = async () => {
            setLoading(true);
            setError('');
            try {
                const [settingsRes, membersRes, projectsRes] = await Promise.all([
                    axiosInstance.get(`accounts/workspaces/${effectiveWorkspaceId}/admin/settings/`),
                    axiosInstance.get(`accounts/workspaces/${effectiveWorkspaceId}/admin/members/`),
                    // axiosInstance.get(`accounts/workspaces/${effectiveWorkspaceId}/admin/projects/`),
                ]);
                let settingsData = settingsRes?.data || null;
                const memberList = (Array.isArray(membersRes?.data?.members) ? membersRes.data.members : []).map(m => ({
                    ...m,
                    is_active: m.is_active !== false,
                }));
                const invitationList = Array.isArray(membersRes?.data?.invitations) ? membersRes.data.invitations : [];
                // const projectList = Array.isArray(projectsRes?.data?.projects) ? projectsRes.data.projects : [];
                const projectTypeList = normalizeProjectTypeOptions(
                    Array.isArray(settingsData?.project_type_options) && settingsData.project_type_options.length
                        ? settingsData.project_type_options
                        : toProjectTypeOptions(DEFAULT_WORKSPACE_PROJECT_TYPES),
                );
                const projectStatusList = Array.isArray(settingsData?.project_status_options) ? settingsData.project_status_options : [];

                // Normalise default lens features and ensure required features are always included.
                const currentFeatures = Array.isArray(settingsData?.default_lens_features) ? settingsData.default_lens_features : [];
                const withRequired = resolveWorkspaceLensFeatures(currentFeatures, REQUIRED_FEATURES);
                settingsData = normalizeWorkspaceProfileFields({
                    ...settingsData,
                    default_lens_features: withRequired,
                    project_type_options: projectTypeList,
                    project_status_options: projectStatusList,
                });

                setSettings(settingsData);
                setOriginalSettings(settingsData);
                setMembers(memberList);
                setInvitations(invitationList);
                setOriginalMembers(memberList);
                // setProjects(projectList);
                setProjectTypes(projectTypeList);
                setProjectStatuses(projectStatusList);
            } catch (e) {
                setError('Failed to load workspace settings.');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [isWorkspaceSuperAdmin, effectiveWorkspaceId, membershipForRoute]);

    useEffect(() => {
        const id = setTimeout(() => setAuditSearch(auditSearchInput), 400);
        return () => clearTimeout(id);
    }, [auditSearchInput]);

    useEffect(() => {
        setAuditPage(1);
    }, [auditSearch]);

    useEffect(() => {
        if (selected !== 'Audit Log') return;
        if (!isWorkspaceSuperAdmin || !effectiveWorkspaceId || !membershipForRoute) return;
        if (auditDateFrom && auditDateTo && auditDateFrom > auditDateTo) {
            setAuditError('End date must be on or after start date.');
            setAuditLogs([]);
            setAuditTotalCount(0);
            setAuditLoading(false);
            return;
        }
        let cancelled = false;
        const loadAudit = async () => {
            setAuditLoading(true);
            setAuditError('');
            try {
                const params = {
                    page: auditPage,
                    page_size: AUDIT_PAGE_SIZE,
                };
                const q = auditSearch.trim();
                if (q) params.search = q;
                if (auditCategory) params.category = auditCategory;
                if (auditDateFrom) params.date_from = auditDateFrom;
                if (auditDateTo) params.date_to = auditDateTo;
                const res = await axiosInstance.get(`accounts/workspaces/${effectiveWorkspaceId}/admin/activity-log/`, {
                    params,
                });
                const list = res?.data?.results ?? res?.data?.activity_logs ?? [];
                const count = typeof res?.data?.count === 'number' ? res.data.count : list.length;
                if (!cancelled) {
                    setAuditLogs(Array.isArray(list) ? list : []);
                    setAuditTotalCount(count);
                }
            } catch (e) {
                if (!cancelled) {
                    setAuditError('Failed to load audit logs.');
                    setAuditLogs([]);
                    setAuditTotalCount(0);
                }
            } finally {
                if (!cancelled) setAuditLoading(false);
            }
        };
        void loadAudit();
        return () => {
            cancelled = true;
        };
    }, [selected, isWorkspaceSuperAdmin, effectiveWorkspaceId, membershipForRoute, auditPage, auditSearch, auditCategory, auditDateFrom, auditDateTo]);

    const formatAuditTimestamp = useCallback(iso => {
        if (!iso) return '';
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return '';
        const dd = String(d.getUTCDate()).padStart(2, '0');
        const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
        const yyyy = d.getUTCFullYear();
        const hh = String(d.getUTCHours()).padStart(2, '0');
        const min = String(d.getUTCMinutes()).padStart(2, '0');
        return `${dd}/${mm}/${yyyy} | ${hh}:${min} GMT`;
    }, []);

    const auditTotalPages = Math.max(1, Math.ceil(auditTotalCount / AUDIT_PAGE_SIZE));
    const auditFilterCount = useMemo(() => {
        let n = 0;
        if (auditSearch.trim()) n += 1;
        if (auditCategory) n += 1;
        if (auditDateFrom || auditDateTo) n += 1;
        return n;
    }, [auditSearch, auditCategory, auditDateFrom, auditDateTo]);

    const clearAuditFilters = useCallback(() => {
        setAuditSearchInput('');
        setAuditSearch('');
        setAuditCategory('');
        setAuditDateFrom('');
        setAuditDateTo('');
        setAuditPage(1);
    }, []);

    const exportAuditCsv = useCallback(async () => {
        if (!effectiveWorkspaceId) return;
        if (auditDateFrom && auditDateTo && auditDateFrom > auditDateTo) {
            toast.error('End date must be on or after start date.');
            return;
        }
        const baseParams = { page_size: 200 };
        const q = auditSearch.trim();
        if (q) baseParams.search = q;
        if (auditCategory) baseParams.category = auditCategory;
        if (auditDateFrom) baseParams.date_from = auditDateFrom;
        if (auditDateTo) baseParams.date_to = auditDateTo;
        const all = [];
        let page = 1;
        try {
            while (true) {
                const res = await axiosInstance.get(`accounts/workspaces/${effectiveWorkspaceId}/admin/activity-log/`, {
                    params: { ...baseParams, page },
                });
                const results = res?.data?.results ?? [];
                all.push(...results);
                if (!res?.data?.next) break;
                page += 1;
            }
            if (!all.length) {
                toast.info('No audit events to export.');
                return;
            }
            const escape = v => {
                const s = String(v ?? '');
                if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
                return s;
            };
            const header = ['Event Type', 'Log', 'Level', 'Timestamp'];
            const lines = [
                header.join(','),
                ...all.map(row =>
                    [
                        escape(row.event_label),
                        escape(row.message),
                        escape(formatAuditCategoryDisplay(row.category)),
                        escape(formatAuditTimestamp(row.created_at)),
                    ].join(','),
                ),
            ];
            const blob = new Blob([`\uFEFF${lines.join('\n')}`], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `workspace-audit-${String(effectiveWorkspaceId || '').slice(0, 8)}-${new Date().toISOString().slice(0, 10)}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error('Export failed.');
        }
    }, [effectiveWorkspaceId, auditSearch, auditCategory, auditDateFrom, auditDateTo, formatAuditTimestamp]);

    const handleDefaultChange = value => {
        setSettings(prev => (prev ? { ...prev, default_has_diligence_tools: value } : { default_has_diligence_tools: value }));
    };

    const handleProfileChange = patch => {
        setSettings(prev => (prev ? { ...prev, ...patch } : prev));
    };

    const handleAccessToggle = (member, nextValue) => {
        const next = typeof nextValue === 'boolean' ? nextValue : !member.has_diligence_tools;
        setMembers(prev => prev.map(m => (m.id === member.id ? { ...m, has_diligence_tools: next } : m)));
    };

    const commitUserRowChanges = async (member, { confirmedDelete = false } = {}) => {
        const nextAccess = editingUserAccess === 'all';
        const nextStatus = editingUserStatus;

        if (nextStatus === 'delete' && !confirmedDelete) return;
        if (!effectiveWorkspaceId) return;

        try {
            if (nextStatus === 'delete' && confirmedDelete) {
                await axiosInstance.delete(`accounts/workspaces/${effectiveWorkspaceId}/admin/members/${member.id}/`);
                setMembers(prev => prev.filter(m => m.id !== member.id));
                setOriginalMembers(prev => prev.filter(m => m.id !== member.id));
                toast.success('User removed from workspace.', { position: 'top-right' });
            } else {
                const nextIsActive = nextStatus === 'active';
                await axiosInstance.patch(`accounts/workspaces/${effectiveWorkspaceId}/admin/members/${member.id}/`, {
                    has_diligence_tools: nextAccess,
                    is_active: nextIsActive,
                });
                const patch = { has_diligence_tools: nextAccess, is_active: nextIsActive };
                setMembers(prev => prev.map(m => (m.id === member.id ? { ...m, ...patch } : m)));
                setOriginalMembers(prev => prev.map(m => (m.id === member.id ? { ...m, ...patch } : m)));
                toast.success('User updated.', { position: 'top-right' });
            }
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.response?.data?.name?.[0] || 'Could not update user. Please try again.';
            toast.error(typeof msg === 'string' ? msg : 'Could not update user. Please try again.', { position: 'top-right' });
        } finally {
            setEditingUserId(null);
        }
    };

    const refreshMembersAndInvites = useCallback(async () => {
        if (!effectiveWorkspaceId) return;
        const membersRes = await axiosInstance.get(`accounts/workspaces/${effectiveWorkspaceId}/admin/members/`);
        const memberList = (Array.isArray(membersRes?.data?.members) ? membersRes.data.members : []).map(m => ({
            ...m,
            is_active: m.is_active !== false,
        }));
        const invitationList = Array.isArray(membersRes?.data?.invitations) ? membersRes.data.invitations : [];
        setMembers(memberList);
        setInvitations(invitationList);
        setOriginalMembers(memberList);
    }, [effectiveWorkspaceId]);

    const handleSendWorkspaceInvite = async () => {
        const email = (inviteEmail || '').trim();
        if (!email || !inviteUserType || inviteSubmitting || !effectiveWorkspaceId) return;
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!regex.test(email)) {
            toast.error('Please enter a valid email address.', { position: 'top-right' });
            return;
        }
        setInviteSubmitting(true);
        try {
            const role = 'WORKSPACE_MEMBER';
            const { data } = await axiosInstance.post(`accounts/workspaces/${effectiveWorkspaceId}/invite/`, {
                email,
                role,
            });
            setInviteEmail('');
            setInviteUserType('');
            await refreshMembersAndInvites();
            const detail = data?.detail || 'Invite sent';
            toast.success(detail, { position: 'top-right' });
        } catch (e) {
            const msg = e?.response?.data?.email || e?.response?.data?.detail || e?.response?.data?.message || 'Could not send invite. Please try again.';
            toast.error(typeof msg === 'string' ? msg : 'Could not send invite. Please try again.', { position: 'top-right' });
        } finally {
            setInviteSubmitting(false);
        }
    };

    const handleInvitationAction = async invite => {
        if (!effectiveWorkspaceId || !invite?.id || invitationActionId === invite.id) return;
        setInvitationActionId(invite.id);
        try {
            await axiosInstance.delete(`accounts/workspaces/${effectiveWorkspaceId}/admin/invitations/${invite.id}/`);

            if (invite.is_expired) {
                await axiosInstance.post(`accounts/workspaces/${effectiveWorkspaceId}/invite/`, {
                    email: invite.email,
                    role: invite.role || 'WORKSPACE_MEMBER',
                    resend: true,
                });
                toast.success('Invitation resent.', { position: 'top-right' });
            } else {
                toast.success('Invitation revoked.', { position: 'top-right' });
            }

            await refreshMembersAndInvites();
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.response?.data?.message || 'Could not update invitation. Please try again.';
            toast.error(typeof msg === 'string' ? msg : 'Could not update invitation. Please try again.', { position: 'top-right' });
        } finally {
            setInvitationActionId(null);
        }
    };

    const handleFeatureToggle = featureKey => {
        // Prevent disabling required workspace features
        if (REQUIRED_FEATURES.includes(featureKey)) {
            return;
        }
        setSettings(prev => {
            const current = Array.isArray(prev?.default_lens_features) ? prev.default_lens_features : [];
            const has = current.includes(featureKey);
            const next = has ? current.filter(k => k !== featureKey) : [...current, featureKey];
            return {
                ...(prev || {}),
                default_lens_features: Array.from(new Set([...REQUIRED_FEATURES, ...next])),
            };
        });
    };

    const handleAddAllFeatures = () => {
        const allKeys = Object.values(FEATURE_CATEGORIES).flatMap(category => Object.keys(category.features));
        setSettings(prev => ({
            ...(prev || {}),
            default_lens_features: Array.from(new Set([...REQUIRED_FEATURES, ...allKeys])),
        }));
    };

    const handleRemoveAllOptionalFeatures = () => {
        setSettings(prev => ({
            ...(prev || {}),
            default_lens_features: [...REQUIRED_FEATURES],
        }));
    };

    const handleRevertLensFeatures = () => {
        if (!originalSettings) return;
        const orig = Array.isArray(originalSettings.default_lens_features) ? originalSettings.default_lens_features : [];
        setSettings(prev => ({
            ...prev,
            default_lens_features: Array.from(new Set([...REQUIRED_FEATURES, ...orig])),
        }));
    };

    const handleProjectTypesChange = nextList => {
        const normalized = normalizeProjectTypeOptions(nextList);
        setProjectTypes(normalized);
        setSettings(prev => ({ ...(prev || {}), project_type_options: normalized }));
    };

    const handleAddProjectStatus = () => {
        const label = (newStatusName || '').trim();
        if (!label) return;
        const color = newStatusColor || '#8E2DE2';
        const nextList = [
            ...(Array.isArray(projectStatuses) ? projectStatuses : []),
            {
                name: label,
                color,
            },
        ];
        setProjectStatuses(nextList);
        setSettings(prev => ({ ...(prev || {}), project_status_options: nextList }));
        setNewStatusName('');
    };

    const handleDeleteProjectStatus = index => {
        const status = projectStatuses[index];
        const nextList = projectStatuses.filter((_, i) => i !== index);
        // status is kept for potential future use (e.g., server-side delete metadata) in handleSave
        void status;
        setProjectStatuses(nextList);
        setSettings(prev => ({ ...(prev || {}), project_status_options: nextList }));
    };

    const startEditProjectStatus = (index, currentName, currentColor) => {
        setEditingStatusIndex(index);
        setEditingStatusName(currentName || '');
        setEditingStatusColor(currentColor || '#8E2DE2');
    };

    const cancelEditProjectStatus = () => {
        setEditingStatusIndex(null);
        setEditingStatusName('');
        setEditingStatusColor('#8E2DE2');
    };

    const handleSaveProjectStatusEdit = index => {
        const label = (editingStatusName || '').trim();
        if (label === '' || index == null) {
            cancelEditProjectStatus();
            return;
        }
        const nextList = projectStatuses.map((st, i) =>
            i === index
                ? {
                      ...st,
                      name: label,
                      color: editingStatusColor || st.color || '#8E2DE2',
                  }
                : st,
        );

        setProjectStatuses(nextList);
        setSettings(prev => ({ ...(prev || {}), project_status_options: nextList }));
        cancelEditProjectStatus();
    };

    const handleStatusDragStart = index => {
        setDraggedStatusIndex(index);
    };

    const handleStatusDragOver = event => {
        event.preventDefault();
    };

    const handleStatusDrop = index => {
        if (draggedStatusIndex === null || draggedStatusIndex === index) return;
        const updated = [...projectStatuses];
        const [moved] = updated.splice(draggedStatusIndex, 1);
        updated.splice(index, 0, moved);
        setDraggedStatusIndex(null);
        setProjectStatuses(updated);
        setSettings(prev => ({ ...(prev || {}), project_status_options: updated }));
    };

    const hasChanges = !!logoFile || JSON.stringify(settings) !== JSON.stringify(originalSettings) || JSON.stringify(members) !== JSON.stringify(originalMembers);

    const handleCancel = () => {
        setSettings(originalSettings);
        setMembers(originalMembers);
        setProjectTypes(originalSettings?.project_type_options || []);
        setProjectStatuses(originalSettings?.project_status_options || []);
        setLogoFile(null);
        if (logoObjectUrl) {
            URL.revokeObjectURL(logoObjectUrl);
        }
        setLogoObjectUrl(null);
    };

    useEffect(() => {
        return () => {
            if (logoObjectUrl) {
                URL.revokeObjectURL(logoObjectUrl);
            }
        };
    }, [logoObjectUrl]);

    const openLogoPicker = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const onLogoChange = e => {
        const f = e.target.files?.[0];
        e.target.value = '';
        if (!f) return;
        const okType = /^image\/(png|jpeg|jpg)$/i.test(f.type);
        if (!okType) {
            toast.error('Please use a PNG or JPG image.', { position: 'top-right' });
            return;
        }
        setLogoFile(f);
        setLogoObjectUrl(prev => {
            if (prev) URL.revokeObjectURL(prev);
            return URL.createObjectURL(f);
        });
    };

    const handleSave = async () => {
        if (!effectiveWorkspaceId || !hasChanges) return;
        setSaving(true);
        try {
            let nextSettings = settings;
            let nextOriginal = originalSettings;

            const identityDirty = !!logoFile || (settings?.name || '').trim() !== (originalSettings?.name || '').trim();
            if (identityDirty && settings && originalSettings) {
                if (!(settings?.name || '').trim()) {
                    toast.error('Workspace name cannot be empty.', { position: 'top-right' });
                    setSaving(false);
                    return;
                }
                const formData = new FormData();
                formData.append('name', (settings?.name || '').trim());
                if (logoFile) {
                    formData.append('workspace_logo', logoFile);
                }
                const res = await axiosInstance.patch(`accounts/workspaces/${effectiveWorkspaceId}/admin/settings/`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                const data = res?.data || {};
                nextSettings = { ...settings, ...data };
                nextOriginal = { ...originalSettings, ...data };
                setSettings(nextSettings);
                setOriginalSettings(nextOriginal);
                setProjectTypes(Array.isArray(data.project_type_options) ? data.project_type_options : nextSettings.project_type_options || []);
                setProjectStatuses(Array.isArray(data.project_status_options) ? data.project_status_options : nextSettings.project_status_options || []);
                dispatch(
                    updateWorkspaceDefaults({
                        workspaceId: effectiveWorkspaceId,
                        patch: {
                            name: data.name,
                            workspace_logo: data.workspace_logo,
                            default_lens_features: data.default_lens_features || [],
                            project_type_options: data.project_type_options || [],
                            project_status_options: data.project_status_options || [],
                        },
                    }),
                );
                const prevPreview = logoObjectUrl;
                setLogoFile(null);
                setLogoObjectUrl(null);
                if (prevPreview) {
                    URL.revokeObjectURL(prevPreview);
                }
            }

            if (nextSettings && nextOriginal) {
                const payload = {};
                if (nextSettings.default_has_diligence_tools !== nextOriginal.default_has_diligence_tools) {
                    payload.default_has_diligence_tools = nextSettings.default_has_diligence_tools;
                }
                const currentFeatures = Array.isArray(nextSettings.default_lens_features) ? nextSettings.default_lens_features : [];
                const originalFeatures = Array.isArray(nextOriginal.default_lens_features) ? nextOriginal.default_lens_features : [];
                if (JSON.stringify(currentFeatures) !== JSON.stringify(originalFeatures)) {
                    payload.default_lens_features = currentFeatures;
                }

                const currentTypes = Array.isArray(nextSettings.project_type_options) ? nextSettings.project_type_options : [];
                const originalTypes = Array.isArray(nextOriginal.project_type_options) ? nextOriginal.project_type_options : [];
                if (JSON.stringify(currentTypes) !== JSON.stringify(originalTypes)) {
                    payload.project_type_options = currentTypes;
                }

                const currentStatuses = Array.isArray(nextSettings.project_status_options) ? nextSettings.project_status_options : [];
                const originalStatuses = Array.isArray(nextOriginal.project_status_options) ? nextOriginal.project_status_options : [];
                if (JSON.stringify(currentStatuses) !== JSON.stringify(originalStatuses)) {
                    payload.project_status_options = currentStatuses;
                }

                for (const key of PROFILE_PATCH_KEYS) {
                    const current =
                        key === 'company_values' ? normalizeCompanyValues(nextSettings[key]) : (nextSettings[key] ?? (key === 'company_values' ? [] : ''));
                    const original =
                        key === 'company_values' ? normalizeCompanyValues(nextOriginal[key]) : (nextOriginal[key] ?? (key === 'company_values' ? [] : ''));
                    if (JSON.stringify(current) !== JSON.stringify(original)) {
                        payload[key] = current;
                    }
                }

                if (Object.keys(payload).length) {
                    const res = await axiosInstance.patch(`accounts/workspaces/${effectiveWorkspaceId}/admin/settings/`, payload);
                    const merged = res?.data || nextSettings;
                    setSettings(merged);
                    setOriginalSettings(merged);
                    setProjectTypes(merged.project_type_options || []);
                    setProjectStatuses(merged.project_status_options || []);

                    dispatch(
                        updateWorkspaceDefaults({
                            workspaceId: effectiveWorkspaceId,
                            patch: {
                                default_lens_features: merged.default_lens_features || [],
                                project_type_options: merged.project_type_options || [],
                                project_status_options: merged.project_status_options || [],
                                org_type: merged.org_type || '',
                                org_description: merged.org_description || '',
                                offerings: merged.offerings || '',
                                ideal_customer_profile: merged.ideal_customer_profile || '',
                                company_values: merged.company_values || [],
                                coaching_style: merged.coaching_style || '',
                                pulse_frequency: merged.pulse_frequency || '',
                                intelligence_sensitivity: merged.intelligence_sensitivity || '',
                                pulse_bias: merged.pulse_bias || '',
                            },
                        }),
                    );
                }
            }

            const memberUpdates = members.filter(m => {
                const original = originalMembers.find(o => o.id === m.id);
                if (!original) return false;
                const origActive = original.is_active !== false;
                const nextActive = m.is_active !== false;
                return original.has_diligence_tools !== m.has_diligence_tools || origActive !== nextActive;
            });

            if (memberUpdates.length) {
                await Promise.all(
                    memberUpdates.map(m =>
                        axiosInstance.patch(`accounts/workspaces/${effectiveWorkspaceId}/admin/members/${m.id}/`, {
                            has_diligence_tools: m.has_diligence_tools,
                            is_active: m.is_active,
                        }),
                    ),
                );
                setOriginalMembers(members);
            }
        } catch (e) {
            const msg = e?.response?.data?.name?.[0] || e?.response?.data?.workspace_logo?.[0] || e?.response?.data?.detail || 'Could not save settings. Please try again.';
            toast.error(typeof msg === 'string' ? msg : 'Could not save settings. Please try again.', { position: 'top-right' });
        } finally {
            setSaving(false);
        }
    };

    const items = useMemo(
        () => ['Workspace', 'Features', 'Project Settings', 'Users', 'Audit Log'],
        [],
    );

    const displayLogoUrl = logoObjectUrl || settings?.workspace_logo;
    const seatsUsed = settings?.seats_used ?? members.length;
    const seatLimit = settings?.user_seat_limit || '∞';
    const defaultAllAccess = !!settings?.default_has_diligence_tools;

    return (
        <div className={styles.editBody}>
            <div className={styles.layoutShell}>
                <div className={styles.sidebarPane}>
                    <button className={styles.backButton} type="button" onClick={() => navigate('/project')}>
                        <span className={styles.backIcon}>
                            <ArrowExit20Regular />
                        </span>{' '}
                        Back to Dashboard
                    </button>
                    <div className={styles.navWrapper}>
                        <div className={styles.navLinkBody}>
                            {items.map(label => (
                                <div
                                    key={label}
                                    className={label === selected ? `${styles.navLinkItem} ${styles.selectedNavItem}` : styles.navLinkItem}
                                    onClick={() => setSelected(label)}>
                                    {label}
                                </div>
                            ))}
                        </div>
                        {/* vertical rule is handled via border-right on container in layout */}
                    </div>
                </div>
                <div className={styles.contentPane}>
                    <div
                        id="containerElement"
                        className={styles.formBodies}
                    >
                        {loading && !settings ? (
                            <div className={styles.centered}>Loading workspace settings…</div>
                        ) : error ? (
                            <div className={styles.error}>{error}</div>
                        ) : (
                            <>
                                {/* <h2 className={styles.workspaceTitle}>{workspaceNames}</h2> */}

                                {selected === 'Workspace' && (
                                    <div className={styles.section} style={{ maxWidth: '720px' }}>
                                        <h3 className={styles.sectionTitle}>Workspace</h3>
                                        <p className={styles.sectionSubtitle}>Manage your workspace identity and configuration</p>

                                        <div className={setupStyles.fieldBlock}>
                                            <div className={setupStyles.labelRow}>
                                                <label className={setupStyles.fieldLabel} htmlFor="ws-settings-workspace-name">
                                                    Workspace Name
                                                </label>
                                            </div>
                                            <div className={setupStyles.nameInput}>
                                                <Input
                                                    id="ws-settings-workspace-name"
                                                    value={settings?.name ?? ''}
                                                    onChange={(e, data) => setSettings(prev => (prev ? { ...prev, name: data.value } : prev))}
                                                    placeholder="Workspace name"
                                                    appearance="outline"
                                                />
                                            </div>
                                        </div>

                                        <div className={setupStyles.fieldBlock}>
                                            <div className="d-flex align-items-center justify-content-between">
                                                <span className={setupStyles.fieldLabel}>Workspace Logo</span>
                                                <button type="button" className={setupStyles.changeLink} onClick={openLogoPicker}>
                                                    Change
                                                </button>
                                            </div>
                                            <input
                                                ref={fileInputRef}
                                                className={setupStyles.hiddenInput}
                                                type="file"
                                                accept="image/png,image/jpeg,image/jpg"
                                                aria-hidden
                                                tabIndex={-1}
                                                onChange={onLogoChange}
                                            />
                                            <div
                                                className={setupStyles.dropzone}
                                                role="button"
                                                tabIndex={0}
                                                onClick={openLogoPicker}
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        openLogoPicker();
                                                    }
                                                }}>
                                                {displayLogoUrl ? (
                                                    <div className={setupStyles.previewWrap}>
                                                        <img className={setupStyles.previewImg} src={displayLogoUrl} alt="" />
                                                    </div>
                                                ) : null}
                                                {!displayLogoUrl && (
                                                    <p className={setupStyles.dropzoneHint}>
                                                        {'Supported file formats: PNG/JPG\nRecommended size 200×200px\n\nYou can add or change this later'}
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        <WorkspaceProfileSection profile={settings} onChange={handleProfileChange} />

                                        {hasChanges && (
                                            <div className={`${styles.formActions} ${styles.stickyActionBar}`}>
                                                <button type="button" className={styles.submitButton} onClick={handleSave} disabled={saving}>
                                                    {saving ? 'Saving…' : 'Save'}
                                                </button>
                                                <span className={styles.linkBtn} onClick={handleCancel}>
                                                    Cancel
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {selected === 'Features' && (
                                    <div className={styles.section} style={{ width: '550px' }}>
                                        <h3 className={styles.sectionTitle}>Default Features</h3>
                                        <p className={styles.sectionSubtitle}>
                                            Manage the features enabled by default across all projects in your workspace. Required features are always on and cannot be changed.
                                            Project Admins can adjust these per project at any time.
                                            <p className="mt-2 mb-5">
                                                {' '}
                                                <strong>
                                                    Feature selection applies to Full Access users only.VDR users have access to Dataroom and Information Requests only.
                                                </strong>
                                            </p>
                                        </p>
                                        <div style={{ maxWidth: '550px' }}>
                                            {Object.entries(FEATURE_CATEGORIES).map(([categoryKey, category]) => (
                                                <div key={categoryKey} className={setupStyles.featureCategory}>
                                                    <h4 className={setupStyles.categoryTitle}>{category.name}</h4>
                                                    {Object.entries(category.features).map(([featureKey, feature]) => {
                                                        const isRequired = REQUIRED_FEATURES.includes(featureKey);
                                                        const isRecommended = RECOMMENDED_FEATURES.includes(featureKey);
                                                        const checked =
                                                            isRequired || (Array.isArray(settings?.default_lens_features) && settings.default_lens_features.includes(featureKey));
                                                        return (
                                                            <label key={featureKey} className={setupStyles.featureItem}>
                                                                <span className={setupStyles.featureIconWrap}>
                                                                    {checked ? (
                                                                        <CheckmarkSquare20Filled className={isRequired ? setupStyles.requiredIcon : setupStyles.checkedIcon} />
                                                                    ) : (
                                                                        <Square20Regular className={setupStyles.uncheckedIcon} />
                                                                    )}
                                                                </span>
                                                                <input
                                                                    type="checkbox"
                                                                    className={setupStyles.featureNativeInput}
                                                                    checked={checked}
                                                                    disabled={isRequired}
                                                                    onChange={() => handleFeatureToggle(featureKey)}
                                                                />
                                                                <span className={setupStyles.featureTextWrap}>
                                                                    <span className={setupStyles.featureTitle}>
                                                                        {feature.name}
                                                                        {isRequired ? <span className={setupStyles.featureMeta}> (Required)</span> : null}
                                                                        {!isRequired && isRecommended ? <span className={setupStyles.featureMeta}> (Recommended)</span> : null}
                                                                    </span>
                                                                    <span className={setupStyles.featureDescription}>{feature.description}</span>
                                                                </span>
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            ))}
                                            {hasChanges && (
                                                <div className={`${setupStyles.step2Actions} ${styles.stickyActionBar}`} style={{ marginTop: '24px' }}>
                                                    <button type="button" className={styles.submitButton} onClick={handleSave} disabled={saving}>
                                                        {saving ? 'Saving…' : 'Save Changes'}
                                                    </button>
                                                    <div className={setupStyles.step2Secondary}>
                                                        <button type="button" className={setupStyles.stepActionLink} onClick={handleAddAllFeatures}>
                                                            Add all
                                                        </button>
                                                        <button type="button" className={setupStyles.stepActionLink} onClick={handleRemoveAllOptionalFeatures}>
                                                            Remove all
                                                        </button>
                                                        <button type="button" className={setupStyles.stepActionLink} onClick={handleRevertLensFeatures}>
                                                            Revert
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {selected === 'Users' && (
                                    <>
                                        <div className={styles.section}>
                                            <h3 className={styles.sectionTitle}>Users</h3>
                                            <p className={styles.sectionSubtitle}>Manage all workspace members. Users can be added here or when setting up a project.</p>
                                            <div>
                                                <p className={styles.projectTypesTitle}>Total Users: {members.length + invitations.length}</p>
                                                <p className={styles.projectTypesSubtitle}>Billing starts from each user's join date.</p>
                                            </div>
                                            <div className={styles.projectTypesMenus}>
                                                <button
                                                    type="button"
                                                    className={usersTab === 'active' ? styles.projectTypesMenuActive : styles.projectTypesMenu}
                                                    onClick={() => setUsersTab('active')}>
                                                    Active Users
                                                </button>
                                                <button
                                                    type="button"
                                                    className={usersTab === 'invitations' ? styles.projectTypesMenuActive : styles.projectTypesMenu}
                                                    onClick={() => setUsersTab('invitations')}>
                                                    Invitations
                                                </button>
                                            </div>

                                            {usersTab === 'active' ? (
                                                <div className={styles.usersTableWrap}>
                                                    <div className={styles.usersTableHeader}>
                                                        <div>
                                                            Name <ArrowSort16Regular color="var(--Neutral-Gray-6)" />
                                                        </div>
                                                        <div>Email address</div>
                                                        <div>
                                                            Organisation <ArrowSort16Regular color="var(--Neutral-Gray-6)" />
                                                        </div>
                                                        <div>
                                                            User Type <ArrowSort16Regular color="var(--Neutral-Gray-6)" />
                                                        </div>
                                                        <div>
                                                            Join Date <ArrowSort16Regular color="var(--Neutral-Gray-6)" />
                                                        </div>
                                                        <div>Status</div>
                                                        <div />
                                                    </div>

                                                    {members.length === 0 ? (
                                                        <div className={styles.centered}>No users in this workspace yet.</div>
                                                    ) : (
                                                        <div className={styles.usersTableBody}>
                                                            {members.map(m => {
                                                                const userType =
                                                                    m.role === 'WORKSPACE_SUPER_ADMIN' ? 'Super Admin' : m.has_diligence_tools ? 'Full Access User' : 'VDR User';
                                                                const displayStatus = m.is_active ? 'Active' : 'Inactive';
                                                                const isEditingType = editingUserId === m.id;
                                                                return (
                                                                    <div key={m.id} className={styles.usersTableRow}>
                                                                        <div>{m.full_name || '--'}</div>
                                                                        <div>{m.email || '--'}</div>
                                                                        <div>{m.organisation || m.company || '--'}</div>
                                                                        <div>
                                                                            {m.role === 'WORKSPACE_SUPER_ADMIN' ? (
                                                                                <span>{userType}</span>
                                                                            ) : isEditingType ? (
                                                                                <select
                                                                                    className={styles.userTypeSelect}
                                                                                    value={editingUserAccess}
                                                                                    onChange={e => setEditingUserAccess(e.target.value)}>
                                                                                    <option value="all">Full Access User</option>
                                                                                    <option value="vdr">VDR User</option>
                                                                                </select>
                                                                            ) : (
                                                                                <span>{userType}</span>
                                                                            )}
                                                                        </div>
                                                                        <div>{m.created_at ? new Date(m.created_at).toLocaleDateString('en-GB') : '--'}</div>
                                                                        <div>
                                                                            {isEditingType ? (
                                                                                <select
                                                                                    className={styles.userTypeSelect}
                                                                                    value={editingUserStatus}
                                                                                    onChange={e => setEditingUserStatus(e.target.value)}>
                                                                                    <option value="active">Active</option>
                                                                                    <option value="deactivate">Inactive</option>
                                                                                    <option value="delete">Delete</option>
                                                                                </select>
                                                                            ) : (
                                                                                <span>{displayStatus}</span>
                                                                            )}
                                                                        </div>
                                                                        <div className={styles.usersRowAction}>
                                                                            {m.role !== 'WORKSPACE_SUPER_ADMIN' ? (
                                                                                isEditingType ? (
                                                                                    editingUserStatus === 'delete' ? (
                                                                                        <DoubleConfirmationModal
                                                                                            handleAction={() => void commitUserRowChanges(m, { confirmedDelete: true })}
                                                                                            message={`Delete ${m.full_name || m.email}? User will be removed from Lens. Their contributions across projects will be retained.`}>
                                                                                            <span className={styles.usersRowSave}>Save</span>
                                                                                        </DoubleConfirmationModal>
                                                                                    ) : (
                                                                                        <button
                                                                                            type="button"
                                                                                            className={styles.usersRowSave}
                                                                                            onClick={() => void commitUserRowChanges(m)}>
                                                                                            Save
                                                                                        </button>
                                                                                    )
                                                                                ) : (
                                                                                    <Edit16Regular
                                                                                        color="var(--Neutral-Gray-6)"
                                                                                        onClick={() => {
                                                                                            setEditingUserId(m.id);
                                                                                            setEditingUserAccess(m.has_diligence_tools ? 'all' : 'vdr');
                                                                                            setEditingUserStatus(m.is_active ? 'active' : 'deactivate');
                                                                                        }}
                                                                                        style={{ cursor: 'pointer' }}
                                                                                    />
                                                                                )
                                                                            ) : null}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <>
                                                    <div className={styles.usersTableWrap}>
                                                        <div className={styles.invitesTableHeader}>
                                                            <div>Email address</div>
                                                            <div>
                                                                User Type <ArrowSort16Regular color="var(--Neutral-Gray-6)" />
                                                            </div>
                                                            <div>Status</div>
                                                            <div />
                                                        </div>

                                                        {invitations.length === 0 ? (
                                                            <div className={styles.centered}>No pending invitations.</div>
                                                        ) : (
                                                            <div className={styles.usersTableBody}>
                                                                {invitations.map(inv => {
                                                                    const inviteType =
                                                                        inv.role === 'WORKSPACE_SUPER_ADMIN'
                                                                            ? 'Super Admin'
                                                                            : inv.role === 'WORKSPACE_MEMBER'
                                                                              ? 'VDR User'
                                                                              : 'Full Access User';
                                                                    const statusText = inv.is_expired
                                                                        ? 'Invitation expired'
                                                                        : `Invited on ${new Date(inv.created_date).toLocaleDateString('en-GB')}`;
                                                                    return (
                                                                        <div key={inv.id} className={styles.invitesTableRow}>
                                                                            <div>{inv.email || '--'}</div>
                                                                            <div>{inviteType}</div>
                                                                            <div>{statusText}</div>
                                                                            <div className={styles.invitesAction}>
                                                                                {invitationActionId === inv.id ? (
                                                                                    <span className={styles.invitesActionBtn}>Working...</span>
                                                                                ) : inv.is_expired ? (
                                                                                    <button
                                                                                        type="button"
                                                                                        className={styles.invitesActionBtn}
                                                                                        onClick={() => void handleInvitationAction(inv)}>
                                                                                        Resend Invitation
                                                                                    </button>
                                                                                ) : (
                                                                                    <DoubleConfirmationModal
                                                                                        type="revoke"
                                                                                        modalOneBtnL="Revoke"
                                                                                        modalTwoBtnR="Revoke"
                                                                                        message={`The invitation to [${inv.email}] will be revoked.`}
                                                                                        handleAction={() => void handleInvitationAction(inv)}>
                                                                                        <span className={styles.invitesActionBtn}>Revoke Invitation</span>
                                                                                    </DoubleConfirmationModal>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className={setupStyles.step4InviteRow}>
                                                        <input
                                                            className={setupStyles.step4EmailInput}
                                                            placeholder="Add email address"
                                                            value={inviteEmail}
                                                            onChange={e => setInviteEmail(e.target.value)}
                                                            type="email"
                                                        />
                                                        <select className={setupStyles.step4UserTypeBtn} value={inviteUserType} onChange={e => setInviteUserType(e.target.value)}>
                                                            <option value="" disabled>
                                                                Select User Type
                                                            </option>
                                                            <option value="FULL_ACCESS">Full Access User</option>
                                                            <option value="VDR">VDR User</option>
                                                        </select>
                                                        <button
                                                            type="button"
                                                            className={setupStyles.step4SendBtn}
                                                            disabled={!inviteEmail.trim() || !inviteUserType || inviteSubmitting}
                                                            onClick={() => void handleSendWorkspaceInvite()}>
                                                            {inviteSubmitting ? 'Sending…' : 'Send Invite'}
                                                        </button>
                                                    </div>
                                                </>
                                            )}

                                            {/* {members.length === 0 ? (
                                                <div className={styles.centered}>No users in this workspace yet.</div>
                                            ) : (
                                                <div className={styles.userList}>
                                                    {members.map(m => (
                                                        <div key={m.id} className={styles.userRow}>
                                                            <div className={styles.userName}>{m.full_name || m.email}</div>
                                                            <select
                                                                className={styles.userSelect}
                                                                value={m.has_diligence_tools ? 'all' : 'vdr'}
                                                                onChange={() => handleAccessToggle(m)}>
                                                                <option value="all">All Access User</option>
                                                                <option value="vdr">VDR User</option>
                                                            </select>
                                                        </div>
                                                    ))}
                                                </div>
                                            )} */}
                                        </div>
                                    </>
                                )}

                                {selected === 'Project Settings' && (
                                    <div className={styles.section} style={{ maxWidth: '680px' }}>
                                        <h3 className={styles.sectionTitle}>Project Setting</h3>
                                        <p className={styles.sectionSubtitle}>
                                            Manage the project types and lifecycle stages for your workspace. Changes apply to new projects going forward and do not affect existing
                                            projects.
                                        </p>
                                        {/* <div>
                                            <h3 className={styles.sectionTitle}>Total Projects {projects.length}</h3>
                                            {projects.length === 0 ? (
                                                <div className={styles.centered}>No projects in this workspace yet.</div>
                                            ) : (
                                                <div className={styles.projectList}>
                                                    <div className={styles.projectHeaderRow}>
                                                        <div className={styles.projectHeaderCell}>Project Name</div>
                                                        <div className={styles.projectHeaderCell}>Admin</div>
                                                    </div>
                                                    {projects.map(p => {
                                                        const projectName = p.project_name || p.name || 'Untitled project';
                                                        const adminName = p.project_admin_full_name || '--';
                                                        return (
                                                            <div key={p.id || projectName} className={styles.projectRow}>
                                                                <div className={styles.projectName}>{projectName}</div>
                                                                <div className={styles.projectAdmin}>{adminName}</div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div> */}

                                        <div className={styles.projectTypesSection}>
                                            <h4 className={styles.projectTypesTitle}>Default Project Types</h4>
                                            <p className={styles.projectTypesSubtitle}>
                                                Define the types of deals your organisation manages. Project Admins select from these when creating a new project.
                                            </p>

                                            <ProjectTypesEditor projectTypes={projectTypes} onChange={handleProjectTypesChange} projects={projects} />
                                        </div>

                                        <div className={styles.projectStatusesSection}>
                                            <h4 className={styles.projectTypesTitle + ' mb-1'}>Project Lifecycle Stages</h4>
                                            <p className={styles.projectStatusesSubtitle + ' mb-5'}>
                                                These stages define your deal lifecycle and appear as columns on your portfolio Kanban board. Drag to reorder.
                                            </p>
                                            {projectStatuses.length === 0 ? (
                                                <div className={styles.centered}>No default project statuses yet.</div>
                                            ) : (
                                                <ul className={styles.statusList}>
                                                    {projectStatuses.map((st, idx) => (
                                                        <li
                                                            key={`${st.name}-${idx}`}
                                                            className={draggedStatusIndex === idx ? `${styles.statusRow} ${styles.statusRowDragging}` : styles.statusRow}
                                                            draggable
                                                            onDragStart={() => handleStatusDragStart(idx)}
                                                            onDragOver={handleStatusDragOver}
                                                            onDrop={() => handleStatusDrop(idx)}>
                                                            <div className={styles.statusMain}>
                                                                {editingStatusIndex !== idx && <span className={styles.statusDragHandle}>⋮⋮</span>}
                                                                {editingStatusIndex === idx ? (
                                                                    <>
                                                                        <input
                                                                            type="text"
                                                                            className={styles.statusEditInput}
                                                                            value={editingStatusName}
                                                                            onChange={e => setEditingStatusName(e.target.value)}
                                                                            onKeyDown={e => {
                                                                                if (e.key === 'Enter') {
                                                                                    e.preventDefault();
                                                                                    handleSaveProjectStatusEdit(idx);
                                                                                } else if (e.key === 'Escape') {
                                                                                    e.preventDefault();
                                                                                    cancelEditProjectStatus();
                                                                                }
                                                                            }}
                                                                        />
                                                                        <div className={styles.statusColorInputWrapper}>
                                                                            <input
                                                                                type="color"
                                                                                className={styles.statusColorInput}
                                                                                value={editingStatusColor}
                                                                                onChange={e => setEditingStatusColor(e.target.value)}
                                                                            />
                                                                        </div>
                                                                    </>
                                                                ) : (
                                                                    <span className={styles.statusName}>{st.name}</span>
                                                                )}
                                                            </div>
                                                            <div className={styles.statusActions}>
                                                                {editingStatusIndex !== idx && (
                                                                    <span className={styles.statusColorDot} style={{ backgroundColor: st.color || '#8E2DE2' }} />
                                                                )}
                                                                {editingStatusIndex === idx ? (
                                                                    <>
                                                                        <button type="button" className={styles.saveTypeButton} onClick={() => handleSaveProjectStatusEdit(idx)}>
                                                                            Save
                                                                        </button>
                                                                        <button type="button" className={styles.deleteTypeButton} onClick={cancelEditProjectStatus}>
                                                                            Cancel
                                                                        </button>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <Edit16Regular
                                                                            className="ms-3"
                                                                            color="var(--Neutral-Gray-6)"
                                                                            type="button"
                                                                            onClick={() => startEditProjectStatus(idx, st.name, st.color)}
                                                                        />
                                                                        <DoubleConfirmationModal
                                                                            handleAction={() => handleDeleteProjectStatus(idx)}
                                                                            message="Deleting the selected project status will remove it from all existing projects in this workspace.">
                                                                            <Delete16Regular className="ms-3" color="var(--Neutral-Gray-6)" type="button" />
                                                                        </DoubleConfirmationModal>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}

                                            <div className={styles.statusInputRow}>
                                                <input
                                                    type="text"
                                                    className={styles.projectTypeInput}
                                                    placeholder="Add New Project Status"
                                                    value={newStatusName}
                                                    onChange={e => setNewStatusName(e.target.value)}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            handleAddProjectStatus();
                                                        }
                                                    }}
                                                />
                                                <div className={styles.statusColorInputWrapper}>
                                                    {' '}
                                                    <input
                                                        type="color"
                                                        className={styles.statusColorInput}
                                                        value={newStatusColor}
                                                        onChange={e => setNewStatusColor(e.target.value)}
                                                    />
                                                </div>
                                                <button type="button" className={styles.addTypeButton} onClick={handleAddProjectStatus} disabled={!newStatusName.trim()}>
                                                    Add
                                                </button>
                                            </div>

                                            {hasChanges && (
                                                <div className={`${styles.formActions} ${styles.stickyActionBar}`}>
                                                    <button type="button" className={styles.submitButton} onClick={handleSave} disabled={saving}>
                                                        {saving ? 'Saving…' : 'Save'}
                                                    </button>
                                                    <span className={styles.linkBtn} onClick={handleCancel}>
                                                        Revert
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {selected === 'Audit Log' && (
                                    <div className={styles.section}>
                                        <h3 className={styles.sectionTitle}>Audit Log</h3>
                                        <p className={styles.sectionSubtitle}>A complete record of workspace-level activity. Exportable for compliance and governance purposes.</p>
                                        <div className="d-flex align-items-center justify-content-end flex-wrap gap-2 mt-3">
                                            {auditFilterCount > 0 ? (
                                                <div className="me-2" role="button">
                                                    <button
                                                        type="button"
                                                        onClick={clearAuditFilters}
                                                        style={{
                                                            border: '1px solid var(--Brand-Secondary)',
                                                            borderRadius: '4px',
                                                            color: 'var(--Brand-Secondary)',
                                                            padding: '4px 12px',
                                                            background: 'transparent',
                                                            fontSize: 14,
                                                            cursor: 'pointer',
                                                        }}>
                                                        Filters Applied <span style={{ marginLeft: 4, fontSize: 16 }}>×</span>
                                                    </button>
                                                </div>
                                            ) : null}
                                            <div className="d-flex align-items-center gap-4">
                                                <Menu className="me-3">
                                                    <MenuTrigger>
                                                        <span className={taskMgmtStyles.linkText} style={{ cursor: 'pointer' }}>
                                                            <Filter20Regular className="me-1" />
                                                            Filter ({auditFilterCount})
                                                        </span>
                                                    </MenuTrigger>
                                                    <MenuPopover style={{ width: '320px', minWidth: '320px' }}>
                                                        <MenuList>
                                                            <div className="p-2">
                                                                <div className="d-flex align-items-center justify-content-between mb-3">
                                                                    <h6 className={taskMgmtStyles.tableFilterHeader + ' mb-0'}>Filter Items By</h6>
                                                                    {auditFilterCount > 0 ? (
                                                                        <span className={taskMgmtStyles.secondaryBtnText} onClick={clearAuditFilters}>
                                                                            Clear All
                                                                        </span>
                                                                    ) : null}
                                                                </div>
                                                                <div className="mb-2">
                                                                    <label className={styles.auditFilterLabel} htmlFor="audit-category-filter">
                                                                        Category
                                                                    </label>
                                                                    <select
                                                                        id="audit-category-filter"
                                                                        className={'form-select form-select-sm ' + taskMgmtStyles.filterSelect}
                                                                        style={{ width: '100%', maxWidth: '100%' }}
                                                                        value={auditCategory}
                                                                        onChange={e => {
                                                                            setAuditCategory(e.target.value);
                                                                            setAuditPage(1);
                                                                        }}>
                                                                        {AUDIT_CATEGORY_OPTIONS.map(opt => (
                                                                            <option key={opt.value || 'all'} value={opt.value}>
                                                                                {opt.label}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                                <div className="d-flex gap-2 mb-2">
                                                                    <div className="flex-fill">
                                                                        <label className={styles.auditFilterLabel} htmlFor="audit-date-from">
                                                                            From date
                                                                        </label>
                                                                        <input
                                                                            id="audit-date-from"
                                                                            type="date"
                                                                            className={'form-control form-control-sm ' + styles.auditDateInput}
                                                                            value={auditDateFrom}
                                                                            onChange={e => {
                                                                                setAuditDateFrom(e.target.value);
                                                                                setAuditPage(1);
                                                                            }}
                                                                        />
                                                                    </div>
                                                                    <div className="flex-fill">
                                                                        <label className={styles.auditFilterLabel} htmlFor="audit-date-to">
                                                                            To date
                                                                        </label>
                                                                        <input
                                                                            id="audit-date-to"
                                                                            type="date"
                                                                            className={'form-control form-control-sm ' + styles.auditDateInput}
                                                                            value={auditDateTo}
                                                                            onChange={e => {
                                                                                setAuditDateTo(e.target.value);
                                                                                setAuditPage(1);
                                                                            }}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className="mb-1">
                                                                    <label className={styles.auditFilterLabel} htmlFor="audit-search-input">
                                                                        Search
                                                                    </label>
                                                                    <Input
                                                                        id="audit-search-input"
                                                                        className="w-100"
                                                                        value={auditSearchInput}
                                                                        onChange={(_, d) => setAuditSearchInput(d.value)}
                                                                        placeholder="Event type, log text, or level"
                                                                    />
                                                                </div>
                                                            </div>
                                                        </MenuList>
                                                    </MenuPopover>
                                                </Menu>
                                            </div>
                                            <div className="d-flex align-items-center ms-3">
                                                <button type="button" className={taskMgmtStyles.regularSecondaryBtn} onClick={exportAuditCsv}>
                                                    Export
                                                </button>
                                                <Menu>
                                                    <MenuTrigger>
                                                        <button type="button" className={taskMgmtStyles.menuButton} aria-label="Export options">
                                                            <ChevronDown12Regular />
                                                        </button>
                                                    </MenuTrigger>
                                                    <MenuPopover>
                                                        <MenuList>
                                                            <MenuItem onClick={exportAuditCsv}>Export audit log as CSV</MenuItem>
                                                        </MenuList>
                                                    </MenuPopover>
                                                </Menu>
                                            </div>
                                        </div>
                                        <div className={styles.auditLogTableWrap}>
                                            {auditLoading ? (
                                                <div className={styles.centered}>Loading audit logs…</div>
                                            ) : auditError ? (
                                                <div className={styles.error}>{auditError}</div>
                                            ) : auditLogs.length === 0 ? (
                                                <div className={styles.centered}>No audit events found.</div>
                                            ) : (
                                                <div className={`${taskMgmtStyles.tableContainer} ${styles.auditLogTableInner}`} style={{ height: 'calc(100vh - 320px)' }}>
                                                    <table className="w-100">
                                                        <thead>
                                                            <tr className={taskMgmtStyles.tableHeader}>
                                                                <th className={`${taskMgmtStyles.headerRowItem} ${styles.auditLogHeaderLeft}`} style={{ width: '20%' }}>
                                                                    Event Type
                                                                </th>
                                                                <th className={`${taskMgmtStyles.headerRowItem} ${styles.auditLogHeaderLeft}`}>Log</th>
                                                                <th className={taskMgmtStyles.headerRowItem} style={{ width: '18%' }}>
                                                                    Category
                                                                </th>
                                                                <th className={taskMgmtStyles.headerRowItem} style={{ width: '18%' }}>
                                                                    Timestamp
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {auditLogs.map(row => (
                                                                <tr key={row.id} className={taskMgmtStyles.tableRow}>
                                                                    <td className={`${taskMgmtStyles.tableRowItem} ${styles.auditLogCellLeft}`}>
                                                                        <div className={`${taskMgmtStyles.taskText} ${styles.auditLogPlainText}`}>{row.event_label}</div>
                                                                    </td>
                                                                    <td className={`${taskMgmtStyles.tableRowItem} ${styles.auditLogCellLeft}`}>
                                                                        <div className={`${taskMgmtStyles.taskText} ${styles.auditLogPlainText} ${styles.auditLogMessageText}`}>
                                                                            {row.message}
                                                                        </div>
                                                                    </td>
                                                                    <td className={taskMgmtStyles.tableRowItem}>
                                                                        <div className={taskMgmtStyles.taskText}>{formatAuditCategoryDisplay(row.category)}</div>
                                                                    </td>
                                                                    <td className={taskMgmtStyles.tableRowItem}>
                                                                        <div className={taskMgmtStyles.taskText}>{formatAuditTimestamp(row.created_at)}</div>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                            {!auditLoading && !auditError && auditTotalCount > 0 ? (
                                                <div className={styles.auditLogPagination}>
                                                    <span className={styles.auditLogPaginationMeta}>
                                                        {(auditPage - 1) * AUDIT_PAGE_SIZE + 1}–{Math.min(auditPage * AUDIT_PAGE_SIZE, auditTotalCount)} of {auditTotalCount}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        className={styles.auditLogPageBtn}
                                                        disabled={auditPage <= 1}
                                                        onClick={() => setAuditPage(p => Math.max(1, p - 1))}>
                                                        Previous
                                                    </button>
                                                    <span className={styles.auditLogPaginationMeta}>
                                                        Page {auditPage} of {auditTotalPages}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        className={styles.auditLogPageBtn}
                                                        disabled={auditPage >= auditTotalPages}
                                                        onClick={() => setAuditPage(p => p + 1)}>
                                                        Next
                                                    </button>
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WorkspaceSettings;
